import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Breakpoint,
    Button,
    ButtonProps,
    DialogTitle,
    IconButton,
    Popover,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { type ReactElement, forwardRef } from 'react';

import { AxleErrorBoundary } from '../../fetching/errorBoundaries';
import { AxleSuspense, PAGE_BODY_SKELETON_HEIGHT } from '../../fetching/suspense';
import { useBooleanState } from '../../utils/booleanState';
import { ClickHandler, dontLetClicksEscape, wrapOnClick } from '../../utils/clickEvent';
import { Children, DefiniteComponent, ReturnComponent, Style } from '../../utils/reactTypes';
import { AxleConfirmDialog } from './AxleConfirmDialogContent';
import { DialogNavigationBlocker } from './DialogNavigationBlocker';

export const TransitionComponent = forwardRef(
    ({ children, ...props }: { children: DefiniteComponent } & TransitionProps, ref) => {
        return (
            <Slide direction='down' ref={ref} {...props}>
                {children}
            </Slide>
        );
    },
);
TransitionComponent.displayName = 'TransitionComponent';

export type AxleDialogProps = {
    isOpen: boolean;
    close: () => void;
    title: string | ReactElement;
    children: Children;
    width?: Breakpoint;
    makeFullscreen?: boolean;
    hideCloseButton?: boolean;
    confirmMessageBeforeClosing?: string;
    onTransitionExited?: () => void;
    dataTestId?: string;
    defaultPadding?: boolean;
    skeletonHeight?: string;
};

// If your dialog is loading data and using Suspense features, then you need to
//  make a separate dialog content component (separate from your dialog component).
// Try to not wrap <AxleDialog/> inside an <If/> component. That way, you get a
//  nice exit animation when the dialog closes, instead of React hiding the
//  component immediately.
export const AxleDialog = ({
    isOpen,
    close,
    title,
    children,
    width = 'sm',
    makeFullscreen = false,
    hideCloseButton = false,
    confirmMessageBeforeClosing,
    onTransitionExited,
    dataTestId,
    defaultPadding,
    skeletonHeight = PAGE_BODY_SKELETON_HEIGHT,
}: AxleDialogProps): ReturnComponent => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('xs')) || makeFullscreen;
    if (confirmMessageBeforeClosing !== undefined) {
        const oldClose = close;
        close = () => {
            const didUserConfirm = window.confirm(confirmMessageBeforeClosing);
            if (didUserConfirm) {
                oldClose();
            }
        };
    }
    return (
        <>
            {confirmMessageBeforeClosing !== undefined && isOpen ? (
                <DialogNavigationBlocker confirmMessage={confirmMessageBeforeClosing} />
            ) : null}
            <Dialog
                TransitionComponent={TransitionComponent}
                onTransitionExited={onTransitionExited}
                maxWidth={width}
                fullWidth
                fullScreen={fullScreen}
                open={isOpen}
                onClose={wrapOnClick(close)}
                scroll={'body'}
                PaperProps={{
                    'data-test-id': dataTestId,
                    sx: {
                        // This is needed to make <AxleDateField/>s inside the dialog,
                        //  (which use react-datepicker internally and show a pop-up)
                        //  to not get cut off by the edges of the dialog.
                        overflow: 'visible !important',
                    },
                }}
            >
                <Box
                    style={defaultPadding ? undefined : { padding: theme.spacing(2, 2) }}
                    // Q: Why do we have this no-op onClick handler?
                    // A: Without this, if a dialog was nested inside a table row with an
                    //    onClick handler, clicking in the body of the dialog would trigger
                    //    that row's onClick handler and maybe cause a confusing redirect.
                    // Q: Why does that happen?
                    // A: The scrollbody inside the dialog seems to have an onClick handler
                    //    that doesn't stop propagation, so if you click on the unused body
                    //    inside a dialog, a click event is triggered and propagated up.
                    // Q: Could we put this fix in `<AxleDialog/>` instead?
                    // A: No, then clicking in the background of dialog to dismiss it doesn't
                    //    work.
                    onClick={dontLetClicksEscape}
                >
                    <AxleDialogTitle
                        defaultPadding={defaultPadding}
                        title={title}
                        close={hideCloseButton ? null : close}
                    />
                    <AxleErrorBoundary>
                        <AxleSuspense skeletonHeight={skeletonHeight} skeletonPadding={2}>
                            {children}
                        </AxleSuspense>
                    </AxleErrorBoundary>
                </Box>
            </Dialog>
        </>
    );
};

export const AxlePopover = ({
    isOpen,
    anchorEl,
    close,
    title,
    children,
    width,
}: {
    isOpen: boolean;
    anchorEl: HTMLElement | null;
    close: () => void;
    title: string | ReactElement;
    children: Children;
    width?: string | undefined;
}): ReturnComponent => {
    const theme = useTheme();
    return (
        <Popover
            open={isOpen}
            anchorEl={anchorEl}
            onClose={close}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            style={{ marginTop: theme.spacing(2) }}
        >
            <Box style={{ width, maxWidth: '95vw' }}>
                <Box style={{ padding: theme.spacing(1, 1) }}>
                    <AxleDialogTitle title={title} close={close} />
                    <AxleErrorBoundary>
                        <AxleSuspense
                            skeletonHeight={'20vh'}
                            skeletonWidth={'20vw'}
                            skeletonPadding={2}
                        >
                            {children}
                        </AxleSuspense>
                    </AxleErrorBoundary>
                </Box>
            </Box>
        </Popover>
    );
};

export const AxleDialogTitleContent = ({
    title,
    close,
}: {
    title: string | ReactElement;
    close: (() => void) | null;
}): ReturnComponent => {
    const theme = useTheme();
    return (
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <Box sx={{ flex: 1 }}>
                {typeof title === 'string' ? (
                    <Typography
                        variant='h1'
                        style={{
                            // This visually aligns the title with the close button.
                            paddingLeft: theme.spacing(1),
                            paddingTop: theme.spacing(1),
                        }}
                    >
                        {title}
                    </Typography>
                ) : (
                    title
                )}
            </Box>
            <Box>
                {close !== null ? (
                    <IconButton aria-label={'Close'} onClick={close}>
                        <CloseIcon />
                    </IconButton>
                ) : null}
            </Box>
        </Box>
    );
};

export const AxleDialogTitle = ({
    title,
    close,
    defaultPadding,
}: {
    defaultPadding?: boolean;
    title: string | ReactElement;
    close: (() => void) | null;
}): ReturnComponent => {
    const theme = useTheme();
    return (
        <DialogTitle
            style={
                defaultPadding
                    ? undefined
                    : {
                          padding: theme.spacing(0),
                      }
            }
        >
            <AxleDialogTitleContent title={title} close={close} />
        </DialogTitle>
    );
};

export const AxleDialogContent = ({
    children,
    style = {},
}: {
    children: Children;
    style?: Style;
}): ReturnComponent => {
    const theme = useTheme();
    return (
        <DialogContent
            style={{
                padding: theme.spacing(3, 2),
                ...style,
            }}
        >
            {children}
        </DialogContent>
    );
};

export const AxleDialogActions = ({ children }: { children: Children }): ReturnComponent => {
    const theme = useTheme();
    return (
        <DialogActions
            style={{
                paddingBottom: theme.spacing(1),
                gap: 2,
            }}
        >
            {children}
        </DialogActions>
    );
};

export const AxleDialogPrimaryButton = ({
    label,
    enabled = true,
    onClick,
    ...rest
}: {
    label: string;
    enabled?: boolean;
    /* Optional so the button can submit a form by id instead, which is how a dialog whose
     * form sits under a Suspense boundary reaches it from the footer above.
     */
    onClick?: ClickHandler;
} & ButtonProps): ReturnComponent => {
    return (
        <Button
            fullWidth
            disabled={!enabled}
            variant='contained'
            color='primary'
            onClick={wrapOnClick(onClick)}
            style={{
                whiteSpace: 'nowrap',
            }}
            {...rest}
        >
            {label}
        </Button>
    );
};

export const AxleDialogPrimaryButtonWithConfirmationDialog = ({
    label,
    enabled = true,
    confirmationText,
    skipConfirmation = false,
    onClick,
}: {
    label: string;
    enabled?: boolean;
    fullWidth?: boolean;
    confirmationText: string;
    skipConfirmation?: boolean;
    onClick: () => void;
}): ReturnComponent => {
    const [isConfirmationDialogOpen, openConfirmationDialog, closeConfirmationDialog] =
        useBooleanState(false);
    return (
        <>
            <AxleConfirmDialog
                isOpen={isConfirmationDialogOpen}
                close={closeConfirmationDialog}
                confirmCta={label}
                onConfirm={() => {
                    onClick();
                    closeConfirmationDialog();
                }}
            >
                {confirmationText}
            </AxleConfirmDialog>
            <AxleDialogPrimaryButton
                label={label}
                enabled={enabled}
                onClick={skipConfirmation ? onClick : openConfirmationDialog}
            />
        </>
    );
};

// 🚨 DEPRECATED — DO NOT USE ANYMORE.
// TODO: Make all usages of this use <AxleDialogDeleteButtonWithConfirmationDialog/> instead.
export const AxleDialogDeleteButtonDeprecated = ({
    label,
    enabled = true,
    fullWidth = true,
    onClick,
}: {
    label: string;
    enabled?: boolean;
    fullWidth?: boolean;
    onClick: () => void;
}): ReturnComponent => {
    return (
        <Button
            fullWidth={fullWidth}
            disabled={!enabled}
            variant='outlined'
            color='error'
            onClick={wrapOnClick(onClick)}
            style={{
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </Button>
    );
};

export const AxleDialogSoftDeleteButton = ({
    label,
    enabled = true,
    fullWidth = true,
    onClick,
}: {
    label: string;
    enabled?: boolean;
    fullWidth?: boolean;
    onClick: () => void;
}): ReturnComponent => {
    return (
        <Button
            fullWidth={fullWidth}
            disabled={!enabled}
            variant='outlined'
            onClick={wrapOnClick(onClick)}
            style={{
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </Button>
    );
};

export const AxleDialogDeleteButtonWithConfirmationDialog = ({
    label,
    enabled = true,
    fullWidth = true,
    confirmationText,
    onClick,
}: {
    label: string;
    enabled?: boolean;
    fullWidth?: boolean;
    confirmationText: string;
    onClick: () => void;
}): ReturnComponent => {
    const [isConfirmationDialogOpen, openConfirmationDialog, closeConfirmationDialog] =
        useBooleanState(false);
    return (
        <>
            <AxleConfirmDialog
                isOpen={isConfirmationDialogOpen}
                close={closeConfirmationDialog}
                confirmCta={label}
                onConfirm={() => {
                    onClick();
                    closeConfirmationDialog();
                }}
            >
                {confirmationText}
            </AxleConfirmDialog>
            <AxleDialogDeleteButtonDeprecated
                label={label}
                fullWidth={fullWidth}
                enabled={enabled}
                onClick={openConfirmationDialog}
            />
        </>
    );
};

export const AxleDialogSoftDeleteButtonWithConfirmationDialog = ({
    label,
    enabled = true,
    fullWidth = true,
    confirmationText,
    onClick,
}: {
    label: string;
    enabled?: boolean;
    fullWidth?: boolean;
    confirmationText: string;
    onClick: () => void;
}): ReturnComponent => {
    const [isConfirmationDialogOpen, openConfirmationDialog, closeConfirmationDialog] =
        useBooleanState(false);
    return (
        <>
            <AxleConfirmDialog
                isOpen={isConfirmationDialogOpen}
                close={closeConfirmationDialog}
                confirmCta={label}
                onConfirm={() => {
                    onClick();
                    closeConfirmationDialog();
                }}
            >
                {confirmationText}
            </AxleConfirmDialog>
            <AxleDialogSoftDeleteButton
                label={label}
                fullWidth={fullWidth}
                enabled={enabled}
                onClick={openConfirmationDialog}
            />
        </>
    );
};
